import { useEffect, useState } from 'react';
import type { SessionState } from '../lib/session.js';
import { desconectarCalendario, leerCalendario, urlConectarCalendario, type EstadoCalendario } from '../lib/calendario-conexion.js';

/**
 * «Conectar Google Calendar» (0056): each user connects their own calendar once and Fluxo para
 * Meet takes the meeting's contact from the event's invitees (Joan, 15-oct: «como Granola»).
 * Google's screen opens in another tab (it cannot run inside GHL's iframe); back here, the state
 * is read again. Hidden while the server has no OAuth client.
 */
export function CalendarioConexion({ session, inicial = null }: { session: SessionState; inicial?: EstadoCalendario | null }) {
  const [estado, setEstado] = useState<EstadoCalendario | null>(inicial);
  const [ocupado, setOcupado] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [enlace, setEnlace] = useState<string | null>(null);
  const esFluxo = session.client.slug === 'fluxo';

  useEffect(() => {
    if (!esFluxo) return;
    let vivo = true;
    const cargar = () => { leerCalendario(session).then((e) => { if (vivo) setEstado(e); }).catch(() => undefined); };
    const alVolver = () => { if (document.visibilityState === 'visible') cargar(); };
    cargar();
    // Back from Google's tab. Inside GHL's iframe `focus` may not fire; `visibilitychange` does.
    window.addEventListener('focus', cargar);
    document.addEventListener('visibilitychange', alVolver);
    return () => { vivo = false; window.removeEventListener('focus', cargar); document.removeEventListener('visibilitychange', alVolver); };
  }, [session, esFluxo]);

  if (!esFluxo || !estado?.disponible) return null;

  async function conectar() {
    // The tab opens on the click itself (popup blockers let it through) and goes to Google when the URL arrives.
    const pestana = window.open('', '_blank');
    setOcupado(true); setMensaje(''); setEnlace(null);
    try {
      const url = await urlConectarCalendario(session);
      if (pestana) {
        pestana.opener = null;
        pestana.location.href = url;
        setMensaje('Acepta en la pestaña de Google y vuelve aquí.');
      } else {
        setEnlace(url);
      }
    } catch (e) {
      pestana?.close();
      setMensaje(e instanceof Error ? e.message : 'No se ha podido conectar Google Calendar.');
    } finally { setOcupado(false); }
  }

  async function desconectar() {
    setOcupado(true); setMensaje(''); setEnlace(null);
    try {
      await desconectarCalendario(session);
      setEstado({ ...estado!, conectado: false, cuenta: null, fallo: null });
      setMensaje('Google Calendar desconectado.');
    } catch (e) { setMensaje(e instanceof Error ? e.message : 'No se ha podido desconectar.'); }
    finally { setOcupado(false); }
  }

  const roto = estado.conectado && estado.fallo !== null;
  return (
    <div className="meet-conexion" role="group" aria-label="Google Calendar">
      {estado.conectado && !roto
        ? <p>Google Calendar conectado: <strong>{estado.cuenta}</strong>. Fluxo para Meet saca el contacto de cada reunión de sus invitados.</p>
        : <p>{roto
          ? `Google Calendar (${estado.cuenta}) ha dejado de responder. Vuelve a conectarlo.`
          : 'Conecta tu Google Calendar y Fluxo para Meet sabrá de quién es cada reunión por sus invitados, sin que tengas que escribir nada.'}</p>}
      <div className="botones">
        {(!estado.conectado || roto) && <button type="button" disabled={ocupado} onClick={conectar}>{ocupado ? 'Espera...' : 'Conectar Google Calendar'}</button>}
        {estado.conectado && <button type="button" disabled={ocupado} onClick={desconectar}>Desconectar</button>}
      </div>
      {enlace && <p><a href={enlace} target="_blank" rel="noopener noreferrer">Abrir la pantalla de Google</a></p>}
      {mensaje && <p role="status">{mensaje}</p>}
    </div>
  );
}
